import { FC, ReactElement, ReactNode, useMemo, useState } from 'react';
import { Avatar, Button, Col, Dropdown, Row, Typography } from 'antd';
import _ from 'lodash';
import { LucideLeaf, LucidePiggyBank, LucideShapes, Tags as TagsIcon, TextQuote } from 'lucide-react';
import {
    TbActivityHeartbeat,
    TbArrowLeft,
    TbArrowsExchange,
    TbAward,
    TbBuilding,
    TbBuildingBank,
    TbBuildingFactory2,
    TbCalendarDollar,
    TbCalendarSearch,
    TbChartDonut,
    TbChartPie,
    TbCrosshair,
    TbDatabase,
    TbDatabaseSearch,
    TbDeviceFloppy,
    TbFileAnalytics,
    TbFileInfo,
    TbFileText,
    TbFoldUp,
    TbHistory,
    TbIdBadge2,
    TbKeyframes,
    TbLanguage,
    TbLayoutGrid,
    TbMapPin2,
    TbMoodSearch,
    TbNews,
    TbNumber123,
    TbPlus,
    TbRefresh,
    TbSchool,
    TbTags,
    TbTagStarred,
    TbTransactionDollar,
    TbUserSearch,
    TbUsersGroup,
    TbWorld,
    TbWorldBolt,
    TbZoomCode,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import type { SearchTabKey } from '@/layouts/02-Authenticated/layout/03-Content/routes/04-Search/searchTabs';
import { runAfterPlatformDragCancellation } from '@/shared/components/drag-and-drop';
import DropdownPanel from '@/shared/components/dropdowns/DropdownPanel';
import {
    ADVANCED_FILTERS_MINIMUM_PLAN,
    createAdvancedFilterDefinition,
    getSearchFilterEntries,
    getSearchFilterOperator,
    isAdvancedFilterDefinitionComplete,
    unwrapSearchFilters,
    type AdvancedFilterDefinition,
    type SearchAdvancedFilterRule,
} from '@/shared/components/filters';
import AdvancedFilterEditor from '@/shared/components/filters/AdvancedFilterEditor';
import FilterEntryButton from '@/shared/components/filters/FilterEntryButton';
import type { SearchFilterChangeHandler } from '@/shared/components/filters/SearchFiltersSider';
import LearnMoreFooter from '@/shared/components/layouts/footers/LearnMoreFooter';
import NotAvailable from '@/shared/components/layouts/views/NotAvailable';
import { isSearchDomainAvailable } from '@/shared/constants';

import AllSearchFilters from '../../../../00-All/components/AllSearchFilters';
import type { AllSearchDomain } from '../../../../00-All/useAllSearch';
import CompanyFilterOptions from '../01-Sider/views/01-FilterOptions';
import { getSearchFilterFieldMetadata } from './filterFieldMetadata';
import AttributeFilterItem from './options/AttributeFilterItem';
import SearchFilterFieldPicker from './SearchFilterFieldPicker';

interface SearchFilterDropdownProps {
    activeCount: number;
    advancedFilter?: AdvancedFilterDefinition<SearchAdvancedFilterRule>;
    filters: unknown;
    handleChange: SearchFilterChangeHandler;
    onAdvancedFilterChange?: (advancedFilter: AdvancedFilterDefinition<SearchAdvancedFilterRule> | undefined) => void;
    openToAdvancedFilters?: boolean;
    tab: SearchTabKey;
    trigger?: ReactElement;
}

interface SearchFilterPickerSection {
    domain: AllSearchDomain;
    group: string;
    icon: ReactNode;
    key: string;
    label: string;
}

const companySections: SearchFilterPickerSection[] = [
    {
        domain: 'companies',
        group: 'Company Filters',
        icon: <TagsIcon aria-hidden size='0.8125rem' />,
        key: 'workspaceTags',
        label: 'Tags',
    },
    { domain: 'companies', group: 'Company Filters', icon: <TbCrosshair />, key: 'watchlists', label: 'Watchlists' },
    {
        domain: 'companies',
        group: 'Company Filters',
        icon: <LucideShapes aria-hidden size='0.8125rem' />,
        key: 'assetClasses',
        label: 'Asset Type',
    },
    { domain: 'companies', group: 'Company Filters', icon: <TbBuildingBank />, key: 'exchanges', label: 'Exchanges' },
    {
        domain: 'companies',
        group: 'Company Filters',
        icon: <TbNumber123 />,
        key: 'identifierCodes',
        label: 'Identifier Codes',
    },
    {
        domain: 'companies',
        group: 'Company Filters',
        icon: <TbBuildingFactory2 />,
        key: 'industries',
        label: 'Industries',
    },
    { domain: 'companies', group: 'Company Filters', icon: <TbChartPie />, key: 'sectors', label: 'Sectors' },
    {
        domain: 'companies',
        group: 'Company Filters',
        icon: <TbMapPin2 />,
        key: 'headquartersLocation',
        label: 'Locations (HQ)',
    },
    { domain: 'companies', group: 'Company Filters', icon: <TbFoldUp />, key: 'fundamentals', label: 'Fundamentals' },
    {
        domain: 'companies',
        group: 'Company Filters',
        icon: <TbCalendarDollar />,
        key: 'fiscalYearEnd',
        label: 'Fiscal Year-End',
    },
    {
        domain: 'companies',
        group: 'Company Filters',
        icon: <TbUsersGroup />,
        key: 'employeeHeadcount',
        label: 'Employee Headcount',
    },
    {
        domain: 'companies',
        group: 'Company Filters',
        icon: <TbChartDonut />,
        key: 'institutionalOwnership',
        label: 'Institutional Ownership',
    },
    { domain: 'companies', group: 'Company Filters', icon: <TbTagStarred />, key: 'companyTags', label: 'Attributes' },
    {
        domain: 'companies',
        group: 'Company Filters',
        icon: <LucideLeaf aria-hidden size='0.8125rem' />,
        key: 'esg',
        label: 'ESG',
    },
    {
        domain: 'companies',
        group: 'Company Filters',
        icon: <TbWorldBolt />,
        key: 'websiteTraffic',
        label: 'Website Traffic',
    },
    {
        domain: 'companies',
        group: 'Company Filters',
        icon: <LucidePiggyBank aria-hidden size='0.8125rem' />,
        key: 'fundraising',
        label: 'Fundraising',
    },
];

const allFederatedSections: SearchFilterPickerSection[] = [
    {
        domain: 'workspace',
        group: 'Workspace Filters',
        icon: <TbLayoutGrid />,
        key: 'Resource Types',
        label: 'Resource Types',
    },
    { domain: 'workspace', group: 'Workspace Filters', icon: <TbTags />, key: 'Tags', label: 'Tags' },
    {
        domain: 'workspace',
        group: 'Workspace Filters',
        icon: <TbHistory />,
        key: 'Last Updated',
        label: 'Last Updated',
    },
    {
        domain: 'people',
        group: 'People Filters',
        icon: <TbIdBadge2 />,
        key: 'Name / Email Address',
        label: 'Name / Email Address',
    },
    {
        domain: 'people',
        group: 'People Filters',
        icon: <TbAward />,
        key: 'Job Title / Role',
        label: 'Job Title / Role',
    },
    { domain: 'people', group: 'People Filters', icon: <TbSchool />, key: 'Education Level', label: 'Education Level' },
    { domain: 'people', group: 'People Filters', icon: <TbZoomCode />, key: 'Keywords', label: 'Keywords' },
    {
        domain: 'people',
        group: 'People Filters',
        icon: <TbUserSearch />,
        key: 'Connected & Enriched Sources',
        label: 'Connected & Enriched Sources',
    },
    { domain: 'documents', group: 'Document Filters', icon: <TbFileInfo />, key: 'metadata', label: 'Metadata' },
    { domain: 'documents', group: 'Document Filters', icon: <TbLanguage />, key: 'language', label: 'Language' },
    { domain: 'documents', group: 'Document Filters', icon: <TbActivityHeartbeat />, key: 'sources', label: 'Sources' },
    { domain: 'documents', group: 'Document Filters', icon: <TbKeyframes />, key: 'keywords', label: 'Keywords' },
    { domain: 'documents', group: 'Document Filters', icon: <TbTags />, key: 'tags', label: 'Tags' },
    { domain: 'documents', group: 'Document Filters', icon: <TbMoodSearch />, key: 'sentiment', label: 'Sentiment' },
    {
        domain: 'documents',
        group: 'Document Coverage',
        icon: <TbFileText />,
        key: 'Document Types',
        label: 'Document Types',
    },
    {
        domain: 'economic',
        group: 'Economic Data Filters',
        icon: <TbTransactionDollar />,
        key: 'Dataset Categories',
        label: 'Dataset Categories',
    },
    {
        domain: 'economic',
        group: 'Economic Data Filters',
        icon: <TbDatabaseSearch />,
        key: 'Sources',
        label: 'Sources',
    },
    { domain: 'economic', group: 'Economic Data Filters', icon: <TbRefresh />, key: 'Frequency', label: 'Frequency' },
    { domain: 'trade', group: 'Global Trade Filters', icon: <TbDatabase />, key: 'sources', label: 'Data Sources' },
    {
        domain: 'trade',
        group: 'Global Trade Filters',
        icon: <TbArrowsExchange />,
        key: 'modes',
        label: 'Transport Modes',
    },
    { domain: 'trade', group: 'Global Trade Filters', icon: <TbWorld />, key: 'regions', label: 'Regions' },
    { domain: 'news', group: 'News Filters', icon: <TbNews />, key: 'News Feeds', label: 'News Feeds' },
    {
        domain: 'news',
        group: 'News Filters',
        icon: <TbFileAnalytics />,
        key: 'Coverage Types',
        label: 'Coverage Types',
    },
    {
        domain: 'events',
        group: 'Event Filters',
        icon: <TbCalendarSearch />,
        key: 'Calendar Sources',
        label: 'Calendar Sources',
    },
    {
        domain: 'events',
        group: 'Event Filters',
        icon: <TbBuilding />,
        key: 'Economic Impact',
        label: 'Economic Impact',
    },
];
const researchSections = ['Coverage', 'Countries', 'Currencies', 'Dividend Symbols'].map((label) => ({
    domain: 'events' as const,
    group: 'Event Filters',
    icon: <TbCalendarSearch />,
    key: label,
    label,
}));
const federatedSections = [...allFederatedSections, ...researchSections].filter((section) =>
    isSearchDomainAvailable(section.domain),
);

const DOMAIN_BY_TAB: Partial<Record<SearchTabKey, AllSearchDomain>> = {
    documents: 'documents',
    economic: 'economic',
    events: 'events',
    news: 'news',
    people: 'people',
    trade: 'trade',
    workspace: 'workspace',
};

const ADVANCED_FILTER_PICKER_ID = '__advanced-filters__';

const availableSectionsForTab = (tab: SearchTabKey): SearchFilterPickerSection[] => {
    const scopedDomain = DOMAIN_BY_TAB[tab];

    if (tab === 'all') {
        return [...companySections.filter((section) => section.key !== 'workspaceTags'), ...federatedSections];
    }
    if (tab === 'companies') {
        return companySections;
    }

    return scopedDomain ? federatedSections.filter((section) => section.domain === scopedDomain) : [];
};

const legacyForRule = (rule?: SearchAdvancedFilterRule) => {
    const legacy: Record<string, unknown> = {};

    if (rule) {
        _.set(legacy, rule.path, _.cloneDeep(rule.value));
    }

    return legacy;
};

const sectionForRule = (
    rule: SearchAdvancedFilterRule | undefined,
    sections: SearchFilterPickerSection[],
): SearchFilterPickerSection | undefined => {
    if (!rule) {
        return undefined;
    }

    const sourcePath = rule.sourcePath ?? [rule.path];
    const domain = sourcePath[0] === 'searchDomains' ? sourcePath[1] : 'companies';
    const key = sourcePath[0] === 'searchDomains' ? sourcePath[2] : sourcePath[0];

    return sections.find((section) => section.domain === domain && section.key === key);
};

interface SearchAdvancedRulePickerProps {
    disabled?: boolean;
    onChange: (rule: SearchAdvancedFilterRule) => void;
    rule?: SearchAdvancedFilterRule;
    tab: SearchTabKey;
    trigger?: ReactElement;
}

const SearchAdvancedRulePicker: FC<SearchAdvancedRulePickerProps> = ({ disabled, onChange, rule, tab, trigger }) => {
    const sections = useMemo(() => availableSectionsForTab(tab), [tab]);
    const [open, setOpen] = useState(false);
    const [selectedSection, setSelectedSection] = useState<SearchFilterPickerSection | undefined>(() =>
        sectionForRule(rule, sections),
    );

    const handleDraftChange: SearchFilterChangeHandler = (path, value) => {
        const nextLegacy = legacyForRule(rule);
        _.set(nextLegacy, path, value);
        const [entry] = getSearchFilterEntries(nextLegacy);

        if (!entry) {
            return;
        }

        onChange({
            ...entry,
            operator:
                rule?.path === entry.path
                    ? rule.operator
                    : getSearchFilterOperator(nextLegacy, entry.path, entry.value),
            sourcePath: path,
        });

        if (!rule) {
            setOpen(false);
            setSelectedSection(undefined);
        }
    };

    const editor = selectedSection ? (
        selectedSection.domain === 'companies' && tab === 'companies' ? (
            <CompanyFilterOptions
                filters={legacyForRule(rule)}
                handleChange={handleDraftChange}
                presentation='editor'
                selectedSectionKey={selectedSection.key}
            />
        ) : (
            <AllSearchFilters
                domains={[selectedSection.domain]}
                filters={legacyForRule(rule)}
                handleChange={handleDraftChange}
                presentation='content'
                selectedFilter={{ domain: selectedSection.domain, key: selectedSection.key }}
            />
        )
    ) : null;

    const overlay = selectedSection ? (
        <DropdownPanel
            contentProps={{ onClick: (event) => event.stopPropagation() }}
            contentStyle={{ padding: '0.75rem' }}
            footer={<LearnMoreFooter topic='advanced search filters' variant='dropdown' />}
            maxHeight='30rem'
            width='28rem'
        >
            <Button
                icon={<TbArrowLeft size='1rem' />}
                onClick={() => setSelectedSection(undefined)}
                size='small'
                type='text'
            >
                Properties
            </Button>
            <div style={{ paddingTop: '0.5rem' }}>{editor}</div>
        </DropdownPanel>
    ) : (
        <SearchFilterFieldPicker
            groupLabel={tab === 'all' ? 'groups' : 'properties'}
            items={sections.map((section) => ({
                group: section.group,
                icon: section.icon,
                id: `${section.domain}:${section.key}`,
                label: section.label,
            }))}
            onSelect={(id) => {
                const [domain, ...keyParts] = id.split(':');
                const key = keyParts.join(':');
                setSelectedSection(sections.find((section) => section.domain === domain && section.key === key));
            }}
        />
    );

    return (
        <Dropdown
            destroyPopupOnHide
            disabled={disabled}
            onOpenChange={(nextOpen) => {
                setOpen(nextOpen);
                if (nextOpen) {
                    setSelectedSection(sectionForRule(rule, sections));
                }
            }}
            open={open}
            popupRender={() => overlay}
            placement='bottomLeft'
            trigger={['click']}
        >
            {trigger ?? (
                <Button disabled={disabled} icon={<TbPlus />} size='small' type='dashed'>
                    Filter
                </Button>
            )}
        </Dropdown>
    );
};

const SearchFilterDropdown: FC<SearchFilterDropdownProps> = ({
    activeCount,
    advancedFilter,
    filters,
    handleChange,
    onAdvancedFilterChange = () => undefined,
    openToAdvancedFilters = false,
    tab,
    trigger,
}) => {
    const { token } = useAntDesignTheme();
    const { hasFeature } = useEntitlements();
    const canUseAdvancedFilters = hasFeature('advancedFilters');
    const [open, setOpen] = useState(false);
    const [selectedSection, setSelectedSection] = useState<SearchFilterPickerSection>();
    const [advancedEditorOpen, setAdvancedEditorOpen] = useState(openToAdvancedFilters);
    const [advancedDraft, setAdvancedDraft] = useState<AdvancedFilterDefinition<SearchAdvancedFilterRule>>(() =>
        createAdvancedFilterDefinition<SearchAdvancedFilterRule>(),
    );
    const availableSections = useMemo(() => availableSectionsForTab(tab), [tab]);

    const renderIconAvatar = (icon: ReactNode, size = 18) => (
        <Avatar
            icon={
                <span style={{ alignItems: 'center', color: token.colorTextTertiary, display: 'inline-flex' }}>
                    {icon}
                </span>
            }
            shape='square'
            size={size}
            style={{ background: token.colorBgContainer, border: `1px solid ${token.colorBorder}` }}
        />
    );

    const editor = selectedSection ? (
        tab === 'companies' ? (
            <CompanyFilterOptions
                filters={unwrapSearchFilters(filters)}
                handleChange={handleChange}
                presentation='editor'
                selectedSectionKey={selectedSection.key}
            />
        ) : (
            <AllSearchFilters
                domains={[selectedSection.domain]}
                filters={filters}
                handleChange={handleChange}
                presentation='content'
                selectedFilter={{ domain: selectedSection.domain, key: selectedSection.key }}
            />
        )
    ) : null;

    const advancedOverlay = (
        <DropdownPanel
            contentProps={{ onClick: (event) => event.stopPropagation() }}
            contentStyle={{ padding: '0.75rem' }}
            footer={<LearnMoreFooter topic='advanced search filters' variant='dropdown' />}
            maxHeight='36rem'
            width='42rem'
        >
            <Row align='middle' justify='space-between' style={{ marginBottom: '0.75rem' }} wrap={false}>
                <Col flex='none'>
                    {!openToAdvancedFilters ? (
                        <Button
                            icon={<TbArrowLeft size='1rem' />}
                            onClick={() => setAdvancedEditorOpen(false)}
                            size='small'
                            type='text'
                        >
                            Properties
                        </Button>
                    ) : null}
                </Col>
                <Col flex='auto' style={{ minWidth: 0, paddingLeft: openToAdvancedFilters ? 0 : '0.5rem' }}>
                    <Typography.Text strong>Advanced Filters</Typography.Text>
                </Col>
            </Row>
            {canUseAdvancedFilters ? (
                <>
                    <AdvancedFilterEditor
                        definition={advancedDraft}
                        onChange={setAdvancedDraft}
                        renderAddCondition={({ disabled, onAdd }) => (
                            <SearchAdvancedRulePicker disabled={disabled} onChange={onAdd} tab={tab} />
                        )}
                        renderCondition={({ condition, remove, update }) => {
                            const section = sectionForRule(condition.rule, availableSections);
                            const metadata = getSearchFilterFieldMetadata(
                                condition.rule.sourcePath?.join('.') ?? condition.rule.path,
                            );

                            return (
                                <AttributeFilterItem
                                    fieldLabel={section?.label ?? metadata?.label}
                                    fieldTriggerWrapper={(fieldTrigger) => (
                                        <SearchAdvancedRulePicker
                                            onChange={update}
                                            rule={condition.rule}
                                            tab={tab}
                                            trigger={fieldTrigger}
                                        />
                                    )}
                                    handleDelete={remove}
                                    label={section?.key ?? condition.rule.key}
                                    onOperatorChange={(operator) => update({ ...condition.rule, operator })}
                                    onValueChange={(value) => update({ ...condition.rule, value })}
                                    operator={condition.rule.operator}
                                    path={condition.rule.path}
                                    value={condition.rule.value}
                                    valueOptions={metadata?.options}
                                    valueSelectionMode={metadata?.selectionMode}
                                />
                            );
                        }}
                    />
                    <Row justify='end' style={{ marginTop: '0.75rem' }}>
                        <Button
                            disabled={!isAdvancedFilterDefinitionComplete(advancedDraft)}
                            icon={<TbDeviceFloppy size='1rem' />}
                            size='small'
                            type='primary'
                            onClick={() => {
                                onAdvancedFilterChange(advancedDraft);
                                setOpen(false);
                            }}
                        >
                            Save Advanced Filter
                        </Button>
                    </Row>
                </>
            ) : (
                <NotAvailable height='18rem' planRequired={ADVANCED_FILTERS_MINIMUM_PLAN} />
            )}
        </DropdownPanel>
    );

    const overlay = advancedEditorOpen ? (
        advancedOverlay
    ) : selectedSection ? (
        <DropdownPanel
            contentProps={{ onClick: (event) => event.stopPropagation() }}
            contentStyle={{ padding: 0 }}
            footer={<LearnMoreFooter topic='search filters' variant='dropdown' />}
            maxHeight='32rem'
            width='28rem'
        >
            <div style={{ padding: '0.75rem' }}>
                <Row align='middle' wrap={false} style={{ marginBottom: '0.75rem' }}>
                    <Col flex='none'>
                        <Button
                            aria-label='Back to filter fields'
                            icon={<TbArrowLeft size='1rem' />}
                            onClick={() => setSelectedSection(undefined)}
                            size='small'
                            type='text'
                        />
                    </Col>
                    <Col flex='none' style={{ marginLeft: '0.25rem' }}>
                        {renderIconAvatar(selectedSection.icon, 20)}
                    </Col>
                    <Col flex='auto' style={{ marginLeft: '0.5rem', minWidth: 0 }}>
                        <div style={{ color: token.colorText, fontWeight: 500 }}>{selectedSection.label}</div>
                        <small style={{ color: token.colorTextTertiary }}>{selectedSection.group}</small>
                    </Col>
                </Row>
                <div
                    style={{
                        border: `1px solid ${token.colorBorderSecondary}`,
                        borderRadius: token.borderRadius,
                        padding: '0.75rem',
                    }}
                >
                    {editor}
                </div>
            </div>
        </DropdownPanel>
    ) : (
        <SearchFilterFieldPicker
            groupLabel={tab === 'all' ? 'groups' : 'properties'}
            items={[
                {
                    group: 'Advanced Filters',
                    icon: <TextQuote size='0.8125rem' />,
                    id: ADVANCED_FILTER_PICKER_ID,
                    label: 'Advanced Filters',
                },
                ...availableSections.map((section) => ({
                    group: section.group,
                    icon: section.icon,
                    id: `${section.domain}:${section.key}`,
                    label: section.label,
                })),
            ]}
            onSelect={(id) => {
                if (id === ADVANCED_FILTER_PICKER_ID) {
                    if (canUseAdvancedFilters) {
                        setAdvancedDraft(createAdvancedFilterDefinition<SearchAdvancedFilterRule>());
                    }
                    setAdvancedEditorOpen(true);
                    return;
                }
                const [domain, ...keyParts] = id.split(':');
                const key = keyParts.join(':');
                const section = availableSections.find(
                    (candidate) => candidate.domain === domain && candidate.key === key,
                );

                if (section) {
                    setSelectedSection(section);
                }
            }}
        />
    );

    return (
        <Dropdown
            destroyPopupOnHide
            onOpenChange={(nextOpen) => {
                if (!nextOpen) {
                    runAfterPlatformDragCancellation(() => {
                        setOpen(false);
                        setAdvancedEditorOpen(false);
                        setSelectedSection(undefined);
                    });
                    return;
                }

                setOpen(true);
                setAdvancedEditorOpen(openToAdvancedFilters);
                if (nextOpen && openToAdvancedFilters) {
                    setAdvancedDraft(advancedFilter ?? createAdvancedFilterDefinition<SearchAdvancedFilterRule>());
                }
            }}
            open={open}
            popupRender={() => overlay}
            placement='bottomLeft'
            trigger={['click']}
        >
            {trigger ?? <FilterEntryButton activeCount={activeCount} />}
        </Dropdown>
    );
};

export default SearchFilterDropdown;
